/**
 * The single owner of the training's progress rules on the page (pure, no React).
 *
 * - Server state (which modules are done, the quiz) comes from TanStack Query
 *   (`["training-progress","i2q-sales"]`, see `@/lib/api/training`).
 * - The position inside a module (slide, finished interactions) is a reducer here.
 * - Interactions never write progress; they report `onComplete` to the player, which
 *   calls these functions and then the write hooks.
 *
 * Gates:
 *   - Next on a slide waits for its `required` interaction.
 *   - Next from the last slide completes the module.
 *   - Next into a later module waits until every earlier lesson is done ("Finish this
 *     module first"). A deep link (Learn this) opens any lesson, and finishing it there
 *     counts.
 *   - The quiz opens when the 8 lessons are done; the closing after a passed quiz.
 *   - Reviewer mode (`?reviewer=1`, only with `see_rules`) opens every gate and writes
 *     nothing.
 */

import type { TrainingProgressView } from "@/lib/api";

import {
  type Course,
  MASTERY_PERCENT,
  type Module,
  type ModuleId,
  QUICK_START_MODULE,
  type Quiz,
  type QuizQuestion,
  type QuizTopic,
  type Slide,
  type TrainingTarget,
} from "../types";

export const FINISH_FIRST_TOAST = "Finish this module first";
export const QUIZ_LOCKED_TEXT = "The knowledge check opens when you have finished the 8 lessons.";
export const CLOSING_LOCKED_TEXT = "This part opens when you have passed the knowledge check.";
export const REVIEWER_ALERT = "Reviewer mode: every module is open. Your progress is not saved.";
export const HALFWAY_TOAST = "Halfway there";
/** The toast when Next is pressed before the slide's required task is done (player and quick start). */
export const FINISH_STEP_TOAST = {
  title: "Finish this step first",
  description: "Do the task on this slide to go on.",
} as const;

// ─── links ───────────────────────────────────────────────────────────────────

/** `/training/<module>?s=<slide>` for a "Learn this" target. */
export function trainingHref(target: Pick<TrainingTarget, "module" | "slide">): string {
  return target.slide
    ? `/training/${target.module}?s=${encodeURIComponent(target.slide)}`
    : `/training/${target.module}`;
}

/** Whether a reviewer may open every gate: `?reviewer=1` AND the `see_rules` capability. */
export function reviewerAllowed(
  capabilities: readonly string[] | null | undefined,
  reviewerParam: string | null | undefined,
): boolean {
  return reviewerParam === "1" && (capabilities ?? []).includes("see_rules");
}

// ─── modules ─────────────────────────────────────────────────────────────────

export function lessonsOf(course: Course): Module[] {
  return course.modules.filter((m) => m.kind === "lesson");
}

export function findModule(course: Course, id: string | undefined): Module | undefined {
  return course.modules.find((m) => m.id === id);
}

/** Module ids completed on the server (or in the browser copy). */
export function doneModules(progress: TrainingProgressView | undefined): Set<string> {
  return new Set(
    (progress?.modules ?? []).filter((m) => m.status === "completed").map((m) => m.module_id),
  );
}

export function lessonsDone(course: Course, done: ReadonlySet<string>): number {
  return lessonsOf(course).filter((m) => done.has(m.id)).length;
}

export function allLessonsDone(course: Course, done: ReadonlySet<string>): boolean {
  return lessonsOf(course).every((m) => done.has(m.id));
}

/** Whether a module can be opened at all (a URL or the menu). Lessons always can. */
export function moduleOpen(
  course: Course,
  done: ReadonlySet<string>,
  id: ModuleId,
  reviewer = false,
): { open: boolean; reason: string | null } {
  if (reviewer) {
    return { open: true, reason: null };
  }
  const mod = findModule(course, id);
  if (!mod) {
    return { open: false, reason: null };
  }
  if (mod.kind === "quiz") {
    return allLessonsDone(course, done)
      ? { open: true, reason: null }
      : { open: false, reason: QUIZ_LOCKED_TEXT };
  }
  if (mod.kind === "closing") {
    const quiz = course.modules.find((m) => m.kind === "quiz");
    return !quiz || done.has(quiz.id)
      ? { open: true, reason: null }
      : { open: false, reason: CLOSING_LOCKED_TEXT };
  }
  return { open: true, reason: null };
}

/** Whether every module before `id` (the lessons) is done. */
export function earlierLessonsDone(
  course: Course,
  done: ReadonlySet<string>,
  id: ModuleId,
): boolean {
  const index = course.modules.findIndex((m) => m.id === id);
  return course.modules
    .slice(0, Math.max(0, index))
    .every((m) => m.kind !== "lesson" || done.has(m.id));
}

export type MenuState = "done" | "current" | "open" | "locked";

/**
 * The menu's view of a module: done, current, open, or locked (shown with a lock; the
 * module after the first unfinished lesson, the quiz before the lessons, the closing
 * before the quiz).
 */
export function menuState(
  course: Course,
  done: ReadonlySet<string>,
  id: ModuleId,
  current: ModuleId | null,
  reviewer = false,
): MenuState {
  if (id === current) {
    return "current";
  }
  if (done.has(id)) {
    return "done";
  }
  if (reviewer) {
    return "open";
  }
  if (!moduleOpen(course, done, id).open) {
    return "locked";
  }
  return earlierLessonsDone(course, done, id) ? "open" : "locked";
}

/** Where Next from the last slide of `from` goes, or the toast that stops it. */
export function nextModule(
  course: Course,
  doneAfter: ReadonlySet<string>,
  from: ModuleId,
  reviewer = false,
): { to: ModuleId | null; toast: string | null } {
  const index = course.modules.findIndex((m) => m.id === from);
  const next = course.modules[index + 1];
  if (!next) {
    return { to: null, toast: null };
  }
  if (reviewer) {
    return { to: next.id, toast: null };
  }
  if (!earlierLessonsDone(course, doneAfter, next.id)) {
    return { to: null, toast: FINISH_FIRST_TOAST };
  }
  if (!moduleOpen(course, doneAfter, next.id).open) {
    return { to: null, toast: FINISH_FIRST_TOAST };
  }
  return { to: next.id, toast: null };
}

/** The module and slide to resume at: the server's resume point, else the first unfinished. */
export function resumeTarget(
  course: Course,
  progress: TrainingProgressView | undefined,
): { module: ModuleId; slide: number } {
  const done = doneModules(progress);
  const resume = progress?.resume_module_id;
  const mod = findModule(course, resume ?? undefined);
  if (mod && !done.has(mod.id) && moduleOpen(course, done, mod.id).open) {
    return {
      module: mod.id,
      slide: Math.min(progress?.resume_slide ?? 0, Math.max(0, mod.slides.length - 1)),
    };
  }
  const first = course.modules.find((m) => !done.has(m.id) && moduleOpen(course, done, m.id).open);
  return { module: first?.id ?? course.modules[0]?.id ?? "welcome", slide: 0 };
}

/** The share of the course done, 0 to 100 (lessons, quiz and closing count one each). */
export function coursePercent(course: Course, done: ReadonlySet<string>): number {
  const total = course.modules.length;
  if (total === 0) {
    return 0;
  }
  const count = course.modules.filter((m) => done.has(m.id)).length;
  return Math.round((count * 100) / total);
}

// ─── slides ──────────────────────────────────────────────────────────────────

export interface SlideState {
  index: number;
  /** Interaction ids finished in this visit. */
  completed: Record<string, true>;
}

export type SlideAction =
  | { type: "goto"; index: number }
  | { type: "complete"; id: string }
  | { type: "reset"; index: number };

export function slideReducer(state: SlideState, action: SlideAction): SlideState {
  switch (action.type) {
    case "goto":
      return { ...state, index: Math.max(0, action.index) };
    case "complete":
      return state.completed[action.id]
        ? state
        : { ...state, completed: { ...state.completed, [action.id]: true } };
    case "reset":
      return { index: Math.max(0, action.index), completed: {} };
    default:
      return state;
  }
}

/** Index of `slideId` in a module, or 0 (unknown or absent). */
export function slideIndexOf(mod: Module, slideId: string | null | undefined): number {
  if (!slideId) {
    return 0;
  }
  const index = mod.slides.findIndex((s) => s.id === slideId);
  return index < 0 ? 0 : index;
}

/** Whether Next is allowed on a slide: its required interaction is finished (or reviewer). */
export function canAdvance(
  slide: Slide | undefined,
  completed: Readonly<Record<string, true>>,
  reviewer = false,
): boolean {
  if (!slide || reviewer) {
    return true;
  }
  const interaction = slide.interaction;
  if (!interaction || !interaction.required) {
    return true;
  }
  return Boolean(completed[interaction.id]);
}

/** Interaction ids already done in an earlier visit, from the module status. */
export function completedFromDone(mod: Module, moduleDone: boolean): Record<string, true> {
  if (!moduleDone) {
    return {};
  }
  const out: Record<string, true> = {};
  for (const slide of mod.slides) {
    if (slide.interaction) {
      out[slide.interaction.id] = true;
    }
  }
  return out;
}

// ─── the quiz ────────────────────────────────────────────────────────────────

/** A small seeded PRNG (mulberry32): the same seed gives the same order. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher-Yates over a copy with a seeded PRNG. Correctness stays on the option. */
export function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const out = [...items];
  const random = seededRandom(seed);
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

/** The questions of one attempt with their options shuffled (seed per attempt). */
export function shuffledQuiz(questions: readonly QuizQuestion[], seed: number): QuizQuestion[] {
  return questions.map((q, index) => ({
    ...q,
    options: seededShuffle(q.options, seed * 31 + index + 1),
  }));
}

export function quizPassed(correct: number, total: number): boolean {
  return total > 0 && correct * 100 >= MASTERY_PERCENT * total;
}

export interface QuizScore {
  correct: number;
  total: number;
  percent: number;
  passed: boolean;
  missedTopics: QuizTopic[];
}

/** Score the chosen option ids (`answers[questionId] = optionId`). */
export function scoreQuiz(
  questions: readonly QuizQuestion[],
  answers: Readonly<Record<string, string>>,
): QuizScore {
  let correct = 0;
  const missed: QuizTopic[] = [];
  for (const q of questions) {
    const chosen = q.options.find((o) => o.id === answers[q.id]);
    if (chosen?.correct) {
      correct += 1;
    } else if (!missed.includes(q.topic)) {
      missed.push(q.topic);
    }
  }
  const total = questions.length;
  return {
    correct,
    total,
    percent: total > 0 ? Math.floor((correct * 100) / total) : 0,
    passed: quizPassed(correct, total),
    missedTopics: missed,
  };
}

/** The hints of the missed topics, each pointing at its module. */
export function topicHints(
  quiz: Quiz,
  missed: readonly QuizTopic[],
): { topic: QuizTopic; module: ModuleId; text: string }[] {
  return missed.map((topic) => ({ topic, ...quiz.topicHints[topic] }));
}

/** The stored module id of a quiz: the final quiz, or the quick-start check. */
export function attemptModuleOf(isQuickStart: boolean): "quiz" | "quick-start" {
  return isQuickStart ? QUICK_START_MODULE : "quiz";
}
